import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  TagIcon,
  PlusIcon,
  XMarkIcon,
  TrashIcon,
  PencilIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, Select, Textarea, PageLoader, StarRating, Modal, ModalBody, ModalFooter } from '../../components/ui';
import { clientService } from '../../services';
import { useUIStore } from '../../stores';

export default function ClientForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { confirm } = useUIStore();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#6B7280');
  const [statusLoading, setStatusLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceColor, setNewSourceColor] = useState('#6B7280');
  const [sourceLoading, setSourceLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      status: '',
      source: '',
      sourceDetails: '',
      industry: '',
      score: 0,
      currency: 'EGP',
      totalRevenue: 0,
      tags: [],
      notes: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      },
      customFields: []
    }
  });

  const { fields: customFields, append: appendCustomField, remove: removeCustomField } = useFieldArray({
    control,
    name: 'customFields'
  });

  const [tagInput, setTagInput] = useState('');
  const tags = watch('tags') || [];

  useEffect(() => {
    fetchStatuses();
    fetchSources();
    if (isEdit) {
      fetchClient();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchStatuses = async () => {
    try {
      const data = await clientService.getStatuses();
      setStatuses(data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const data = await clientService.getSources();
      setSources(data);
    } catch (error) {
      console.error('Failed to fetch sources:', error);
    }
  };

  const handleCreateStatus = async () => {
    if (!newStatusName.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    setStatusLoading(true);
    try {
      await clientService.createStatus({
        name: newStatusName.trim(),
        color: newStatusColor
      });
      toast.success(t('clients.statusCreated') || 'Status created successfully');
      setNewStatusName('');
      setNewStatusColor('#6B7280');
      await fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleUpdateStatus = async (statusId) => {
    if (!newStatusName.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    setStatusLoading(true);
    try {
      await clientService.updateStatus(statusId, {
        name: newStatusName.trim(),
        color: newStatusColor
      });
      toast.success(t('clients.statusUpdated') || 'Status updated successfully');
      setEditingStatus(null);
      setNewStatusName('');
      setNewStatusColor('#6B7280');
      await fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteStatus = async (statusId, statusName) => {
    const confirmed = await confirm({
      title: t('common.delete') || 'Delete Status',
      message: t('clients.confirmDeleteStatus', { name: statusName }) || `Are you sure you want to delete "${statusName}"? All clients with this status will be moved to the default status.`,
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) {
      return;
    }

    setStatusLoading(true);
    try {
      await clientService.deleteStatus(statusId);
      toast.success(t('clients.statusDeleted') || 'Status deleted successfully');
      await fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const openEditStatus = (status) => {
    setEditingStatus(status);
    setNewStatusName(status.name);
    setNewStatusColor(status.color || '#6B7280');
    setShowStatusModal(true);
  };

  const openCreateStatus = () => {
    setEditingStatus(null);
    setNewStatusName('');
    setNewStatusColor('#6B7280');
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setEditingStatus(null);
    setNewStatusName('');
    setNewStatusColor('#6B7280');
  };

  const handleCreateSource = async () => {
    if (!newSourceName.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    setSourceLoading(true);
    try {
      await clientService.createSource({
        name: newSourceName.trim(),
        color: newSourceColor
      });
      toast.success(t('clients.sourceCreated') || 'Source created successfully');
      setNewSourceName('');
      setNewSourceColor('#6B7280');
      await fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSourceLoading(false);
    }
  };

  const handleUpdateSource = async (sourceId) => {
    if (!newSourceName.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    setSourceLoading(true);
    try {
      await clientService.updateSource(sourceId, {
        name: newSourceName.trim(),
        color: newSourceColor
      });
      toast.success(t('clients.sourceUpdated') || 'Source updated successfully');
      setEditingSource(null);
      setNewSourceName('');
      setNewSourceColor('#6B7280');
      await fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSourceLoading(false);
    }
  };

  const handleDeleteSource = async (sourceId, sourceName) => {
    const confirmed = await confirm({
      title: t('common.delete') || 'Delete Source',
      message: t('clients.confirmDeleteSource', { name: sourceName }) || `Are you sure you want to delete "${sourceName}"? All clients with this source will be moved to the default source.`,
      confirmText: t('common.delete') || 'Delete',
      cancelText: t('common.cancel') || 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) {
      return;
    }

    setSourceLoading(true);
    try {
      await clientService.deleteSource(sourceId);
      toast.success(t('clients.sourceDeleted') || 'Source deleted successfully');
      await fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSourceLoading(false);
    }
  };

  const openEditSource = (source) => {
    setEditingSource(source);
    setNewSourceName(source.name);
    setNewSourceColor(source.color || '#6B7280');
    setShowSourceModal(true);
  };

  const openCreateSource = () => {
    setEditingSource(null);
    setNewSourceName('');
    setNewSourceColor('#6B7280');
    setShowSourceModal(true);
  };

  const closeSourceModal = () => {
    setShowSourceModal(false);
    setEditingSource(null);
    setNewSourceName('');
    setNewSourceColor('#6B7280');
  };

  const fetchClient = async () => {
    try {
      const client = await clientService.getClient(id);
      reset({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        status: client.status || '',
        source: client.source || '',
        sourceDetails: client.sourceDetails || '',
        industry: client.industry || '',
        score: client.score || 0, // Score is now 0-5 (stars)
        currency: client.currency || 'EGP',
        totalRevenue: client.totalRevenue || 0,
        tags: client.tags || [],
        notes: client.notes || '',
        address: {
          street: client.address?.street || '',
          city: client.address?.city || '',
          state: client.address?.state || '',
          country: client.address?.country || '',
          postalCode: client.address?.postalCode || ''
        },
        customFields: client.customFields || []
      });
      if (client.photo) {
        setPhotoPreview(client.photo);
      }
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const currentTags = watch('tags') || [];
      reset({ ...watch(), tags: [...currentTags, trimmedTag] });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = watch('tags') || [];
    reset({ ...watch(), tags: currentTags.filter(tag => tag !== tagToRemove) });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const addCustomField = () => {
    appendCustomField({
      key: '',
      label: '',
      type: 'text',
      value: ''
    });
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      // Clean the data - convert empty strings to null for optional fields
      const clientData = {
        name: data.name.trim(),
        email: data.email?.trim() || null,
        phone: data.phone?.trim() || null,
        company: data.company?.trim() || null,
        status: data.status || null,
        source: data.source || null,
        sourceDetails: data.sourceDetails?.trim() || null,
        industry: data.industry?.trim() || null,
        score: data.score || 0,
        currency: data.currency || 'EGP',
        totalRevenue: data.totalRevenue || 0,
        tags: data.tags?.filter(tag => tag && tag.trim()) || [],
        notes: data.notes?.trim() || null,
        address: {
          street: data.address?.street?.trim() || null,
          city: data.address?.city?.trim() || null,
          state: data.address?.state?.trim() || null,
          country: data.address?.country?.trim() || null,
          postalCode: data.address?.postalCode?.trim() || null
        },
        customFields: data.customFields?.filter(field =>
          field && field.label && field.label.trim()
        ).map(field => {
          // Auto-generate key from label (lowercase, no spaces, alphanumeric only)
          const label = field.label.trim();
          const key = field.key?.trim() || label
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '')
            .replace(/[^a-z0-9]/g, '');

          return {
            key: key || 'field', // Fallback if label is empty
            label: label,
            type: field.type || 'text',
            value: field.value !== undefined && field.value !== '' ? field.value : null
          };
        }) || []
      };

      // Remove address if all fields are null
      if (Object.values(clientData.address).every(v => v === null)) {
        clientData.address = {};
      }

      if (isEdit) {
        await clientService.updateClient(id, clientData);
        // Upload photo if it's a new file (not from URL)
        if (photoPreview && photoPreview.startsWith('data:image')) {
          // Extract file from data URL and upload
          const response = await fetch(photoPreview);
          const blob = await response.blob();
          const file = new File([blob], 'photo.jpg', { type: blob.type });
          await clientService.uploadPhoto(id, file);
        }
        toast.success(t('clients.updated'));
      } else {
        const newClient = await clientService.createClient(clientData);
        // Upload photo if provided
        if (photoPreview && photoPreview.startsWith('data:image')) {
          const response = await fetch(photoPreview);
          const blob = await response.blob();
          const file = new File([blob], 'photo.jpg', { type: blob.type });
          await clientService.uploadPhoto(newClient._id, file);
        }
        toast.success(t('clients.created'));
      }
      navigate('/clients');
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to translate source name
  const translateSource = (sourceName) => {
    // Map common source names to translation keys
    const sourceMap = {
      'website': t('clients.sources.website'),
      'referral': t('clients.sources.referral'),
      'social': t('clients.sources.social'),
      'advertisement': t('clients.sources.advertisement'),
      'cold_call': t('clients.sources.coldCall'),
      'event': t('clients.sources.event'),
      'other': t('clients.sources.other')
    };

    // Return translated name if available, otherwise return original name
    return sourceMap[sourceName] || sourceName;
  };

  const sourceOptions = [
    { value: '', label: t('common.select') || 'Select...' },
    ...sources.map(s => ({
      value: s.name,
      label: translateSource(s.name)
    }))
  ];

  // Helper function to translate status name
  const translateStatus = (statusName) => {
    // Map common status names to translation keys
    const statusMap = {
      'new': t('clients.statuses.new'),
      'Follow-up': t('clients.statuses.followUp'),
      'Buyer': t('clients.statuses.buyer'),
      'Not Interested': t('clients.statuses.notInterested')
    };

    // Return translated name if available, otherwise return original name
    return statusMap[statusName] || statusName;
  };

  const statusOptions = statuses.map(s => ({
    value: s.name,
    label: translateStatus(s.name)
  }));

  const currencyOptions = [
    { value: 'EGP', label: 'EGP (E£)' },
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'SAR', label: 'SAR (﷼)' },
    { value: 'AED', label: 'AED (د.إ)' },
  ];

  const customFieldTypeOptions = [
    { value: 'text', label: t('common.text') || 'Text' },
    { value: 'number', label: t('common.number') || 'Number' },
    { value: 'date', label: t('common.date') || 'Date' },
    { value: 'boolean', label: t('common.boolean') || 'Boolean' },
    { value: 'select', label: t('common.select') || 'Select' }
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <Helmet>
        <title>{isEdit ? t('clients.editClient') : t('clients.newClient')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={isEdit ? t('clients.editClient') : t('clients.newClient')}
        backUrl="/clients"
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <UserIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('clients.basicInformation') || 'Basic Information'}
              </h2>
            </div>

            {/* Photo Upload */}
            <div className="mb-6">
              <label className="label mb-2">
                {t('clients.photo') || 'Client Photo'}
              </label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt={t('clients.photoPreview') || 'Photo preview'}
                      className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                      <UserIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) {
                        // Reset input value to allow selecting the same file again
                        e.target.value = '';
                        return;
                      }

                      if (file.size > 5 * 1024 * 1024) {
                        toast.error(t('errors.fileTooBig') || 'File size must be less than 5MB');
                        e.target.value = '';
                        return;
                      }

                      // Validate file type
                      if (!file.type.startsWith('image/')) {
                        toast.error(t('errors.invalidFileType') || 'Please select an image file');
                        e.target.value = '';
                        return;
                      }

                      // Show preview
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPhotoPreview(reader.result);
                      };
                      reader.onerror = () => {
                        toast.error(t('errors.serverError') || 'Failed to read file');
                        e.target.value = '';
                      };
                      reader.readAsDataURL(file);

                      // Upload if editing existing client
                      if (isEdit && id) {
                        setPhotoUploading(true);
                        try {
                          const result = await clientService.uploadPhoto(id, file);
                          setPhotoPreview(result.photo);
                          toast.success(t('clients.photoUploaded') || 'Photo uploaded successfully');
                        } catch (error) {
                          toast.error(error.response?.data?.message || t('errors.serverError'));
                          setPhotoPreview(null);
                        } finally {
                          setPhotoUploading(false);
                          // Reset input value to allow selecting the same file again
                          e.target.value = '';
                        }
                      } else {
                        // For new clients, just show preview - will be uploaded on form submit
                        // Reset input value to allow selecting the same file again
                        e.target.value = '';
                      }
                    }}
                    disabled={photoUploading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={photoUploading}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    {photoUploading ? t('common.uploading') || 'Uploading...' : t('clients.uploadPhoto') || 'Upload Photo'}
                  </Button>
                  {photoPreview && isEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        if (!window.confirm(t('clients.confirmDeletePhoto') || 'Are you sure you want to delete this photo?')) {
                          return;
                        }
                        try {
                          await clientService.deletePhoto(id);
                          setPhotoPreview(null);
                          toast.success(t('clients.photoDeleted') || 'Photo deleted successfully');
                        } catch (error) {
                          toast.error(error.response?.data?.message || t('errors.serverError'));
                        }
                      }}
                    >
                      {t('clients.deletePhoto') || 'Delete Photo'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('common.name')}
                required
                icon={UserIcon}
                placeholder={t('clients.namePlaceholder') || 'Enter client name'}
                error={errors.name?.message}
                {...register('name', { required: t('errors.required') })}
              />

              <Input
                label={t('common.email')}
                type="email"
                icon={EnvelopeIcon}
                placeholder={t('clients.emailPlaceholder') || 'client@example.com'}
                error={errors.email?.message}
                {...register('email', {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('errors.invalidEmail')
                  }
                })}
              />

              <Input
                label={t('common.phone')}
                icon={PhoneIcon}
                placeholder={t('clients.phonePlaceholder') || '+1234567890'}
                error={errors.phone?.message}
                {...register('phone')}
              />

              <Input
                label={t('clients.company')}
                icon={BuildingOfficeIcon}
                placeholder={t('clients.companyPlaceholder') || 'Company name'}
                error={errors.company?.message}
                {...register('company')}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-2">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="label">
                      {t('common.status')}
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={openCreateStatus}
                      className="text-xs"
                    >
                      <Cog6ToothIcon className="w-4 h-4 mr-1" />
                      {t('clients.manageStatuses') || 'Manage'}
                    </Button>
                  </div>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={statusOptions}
                        placeholder={t('common.select') || 'Select status'}
                        error={errors.status?.message}
                        {...field}
                      />
                    )}
                  />
                </div>

                <div>
                  <label className="label">
                    {t('clients.score') || 'Score'}
                  </label>
                  <input
                    type="hidden"
                    {...register('score', {
                      min: { value: 0, message: t('errors.minValue', { min: 0 }) },
                      max: { value: 5, message: t('errors.maxValue', { max: 5 }) }
                    })}
                  />
                  <StarRating
                    value={watch('score') || 0}
                    max={5}
                    onChange={(rating) => {
                      const currentValues = watch();
                      reset({ ...currentValues, score: rating });
                    }}
                    size="lg"
                    className="mt-2"
                  />
                  {errors.score?.message && (
                    <p className="mt-1 text-sm text-danger">{errors.score.message}</p>
                  )}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Contact & Business Information */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <BuildingOfficeIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('clients.businessInformation') || 'Business Information'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label">
                    {t('clients.source')}
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={openCreateSource}
                    className="text-xs"
                  >
                    <Cog6ToothIcon className="w-4 h-4 mr-1" />
                    {t('clients.manageSources') || 'Manage'}
                  </Button>
                </div>
                <Controller
                  name="source"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={sourceOptions}
                      placeholder={t('common.select') || 'Select source'}
                      error={errors.source?.message}
                      {...field}
                    />
                  )}
                />
              </div>

              <Input
                label={t('clients.sourceDetails') || 'Source Details'}
                placeholder={t('clients.sourceDetailsPlaceholder') || 'Additional source information'}
                error={errors.sourceDetails?.message}
                {...register('sourceDetails')}
              />

              <Input
                label={t('clients.industry')}
                placeholder={t('clients.industryPlaceholder') || 'e.g., Technology, Healthcare'}
                error={errors.industry?.message}
                {...register('industry')}
              />

              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <TagIcon className="w-5 h-5 text-gray-400" />
                  <label className="label">{t('common.tags') || 'Tags'}</label>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-danger"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder={t('clients.addTagPlaceholder') || 'Add tag and press Enter'}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTag}
                    className="flex-shrink-0"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    {t('common.add') || 'Add'}
                  </Button>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Address Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <MapPinIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('common.address') || 'Address'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('clients.street') || 'Street'}
                placeholder={t('clients.streetPlaceholder') || 'Street address'}
                containerClassName="md:col-span-2"
                error={errors.address?.street?.message}
                {...register('address.street')}
              />

              <Input
                label={t('clients.city') || 'City'}
                placeholder={t('clients.cityPlaceholder') || 'City'}
                error={errors.address?.city?.message}
                {...register('address.city')}
              />

              <Input
                label={t('clients.state') || 'State/Province'}
                placeholder={t('clients.statePlaceholder') || 'State or Province'}
                error={errors.address?.state?.message}
                {...register('address.state')}
              />

              <Input
                label={t('clients.country') || 'Country'}
                placeholder={t('clients.countryPlaceholder') || 'Country'}
                error={errors.address?.country?.message}
                {...register('address.country')}
              />

              <Input
                label={t('clients.postalCode') || 'Postal Code'}
                placeholder={t('clients.postalCodePlaceholder') || 'Postal/ZIP code'}
                error={errors.address?.postalCode?.message}
                {...register('address.postalCode')}
              />
            </div>
          </CardBody>
        </Card>

        {/* Financial Information */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <BuildingOfficeIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('clients.financialInformation') || 'Financial Information'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('clients.currency') || 'Currency'}
                    options={currencyOptions}
                    error={errors.currency?.message}
                    {...field}
                  />
                )}
              />

              <Input
                label={t('clients.totalRevenue') || 'Total Revenue'}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                error={errors.totalRevenue?.message}
                {...register('totalRevenue', {
                  min: { value: 0, message: t('errors.minValue', { min: 0 }) }
                })}
              />
            </div>
          </CardBody>
        </Card>

        {/* Custom Fields Section */}
        <Card>
          <CardBody>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TagIcon className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-neutral-dark">
                  {t('clients.customFields') || 'Custom Fields'}
                </h2>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustomField}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {t('clients.addCustomField') || 'Add Field'}
              </Button>
            </div>

            {customFields.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                {t('clients.noCustomFields') || 'No custom fields added. Click "Add Field" to create one.'}
              </p>
            ) : (
              <div className="space-y-4">
                {customFields.map((field, index) => {
                  // Auto-generate key from label
                  const labelValue = watch(`customFields.${index}.label`) || '';
                  const generatedKey = labelValue
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, '')
                    .replace(/[^a-z0-9]/g, '');

                  // Use existing key if available, otherwise use generated key
                  const currentKey = watch(`customFields.${index}.key`) || generatedKey;

                  return (
                    <div key={field.id} className="p-4 border border-gray-200 rounded-lg">
                      {/* Hidden input to store the key */}
                      <input
                        type="hidden"
                        {...register(`customFields.${index}.key`)}
                        value={currentKey}
                      />
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                        <div className="md:col-span-4">
                          <Input
                            label={t('clients.fieldLabel') || 'Label'}
                            placeholder="e.g., Website URL"
                            error={errors.customFields?.[index]?.label?.message}
                            {...register(`customFields.${index}.label`, {
                              required: t('errors.required'),
                              onChange: (e) => {
                                // Auto-update key when label changes
                                const currentLabel = e.target.value || '';
                                const newKey = currentLabel
                                  .toLowerCase()
                                  .trim()
                                  .replace(/\s+/g, '')
                                  .replace(/[^a-z0-9]/g, '');
                                const currentValues = watch();
                                const updatedFields = [...currentValues.customFields];
                                updatedFields[index] = { ...updatedFields[index], key: newKey };
                                reset({ ...currentValues, customFields: updatedFields });
                              }
                            })}
                          />
                          {generatedKey && (
                            <p className="mt-1 text-xs text-gray-500">
                              {t('clients.autoGeneratedKey') || 'Key'}: <span className="font-mono">{generatedKey}</span>
                            </p>
                          )}
                        </div>

                        <div className="md:col-span-3">
                          <Controller
                            name={`customFields.${index}.type`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                label={t('clients.fieldType') || 'Type'}
                                options={customFieldTypeOptions}
                                error={errors.customFields?.[index]?.type?.message}
                                {...field}
                              />
                            )}
                          />
                        </div>

                        <div className="md:col-span-4">
                          <Input
                            label={t('clients.fieldValue') || 'Value'}
                            placeholder={t('clients.fieldValuePlaceholder') || 'Enter value'}
                            error={errors.customFields?.[index]?.value?.message}
                            {...register(`customFields.${index}.value`)}
                          />
                        </div>

                        <div className="md:col-span-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeCustomField(index)}
                            className="w-full"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Notes Section */}
        <Card>
          <CardBody>
            <div className="flex items-center gap-2 mb-6">
              <TagIcon className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-neutral-dark">
                {t('common.notes')}
              </h2>
            </div>

            <Textarea
              rows={6}
              placeholder={t('clients.notesPlaceholder') || 'Add any additional notes about this client...'}
              error={errors.notes?.message}
              {...register('notes')}
            />
          </CardBody>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/clients')}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </form>

      {/* Status Management Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={closeStatusModal}
        title={editingStatus ? t('clients.editStatus') || 'Edit Status' : t('clients.addStatus') || 'Add Status'}
        size="md"
      >
        <ModalBody>
          <div className="space-y-4">
            <Input
              label={t('common.name')}
              value={newStatusName}
              onChange={(e) => setNewStatusName(e.target.value)}
              placeholder={t('clients.statusNamePlaceholder') || 'Enter status name'}
              required
            />

            <div>
              <label className="label">
                {t('clients.statusColor') || 'Color'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newStatusColor}
                  onChange={(e) => setNewStatusColor(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={newStatusColor}
                  onChange={(e) => setNewStatusColor(e.target.value)}
                  placeholder="#6B7280"
                  className="flex-1"
                />
              </div>
            </div>

            {statuses.length > 0 && (
              <div>
                <label className="label mb-2">
                  {t('clients.existingStatuses') || 'Existing Statuses'}
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {statuses.map((status) => (
                    <div
                      key={status._id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="font-medium">{translateStatus(status.name)}</span>
                        {status.isSystem && (
                          <span className="text-xs text-gray-500">({t('clients.systemStatus') || 'System'})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!status.isSystem && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditStatus(status)}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStatus(status._id, status.name)}
                              disabled={statusLoading}
                            >
                              <TrashIcon className="w-4 h-4 text-danger" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={closeStatusModal}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={editingStatus ? () => handleUpdateStatus(editingStatus._id) : handleCreateStatus}
            loading={statusLoading}
          >
            {editingStatus ? t('common.update') : t('common.create')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Source Management Modal */}
      <Modal
        isOpen={showSourceModal}
        onClose={closeSourceModal}
        title={editingSource ? t('clients.editSource') || 'Edit Source' : t('clients.addSource') || 'Add Source'}
        size="md"
      >
        <ModalBody>
          <div className="space-y-4">
            <Input
              label={t('common.name')}
              value={newSourceName}
              onChange={(e) => setNewSourceName(e.target.value)}
              placeholder={t('clients.sourceNamePlaceholder') || 'Enter source name'}
              required
            />

            <div>
              <label className="label">
                {t('clients.sourceColor') || 'Color'}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newSourceColor}
                  onChange={(e) => setNewSourceColor(e.target.value)}
                  className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <Input
                  value={newSourceColor}
                  onChange={(e) => setNewSourceColor(e.target.value)}
                  placeholder="#6B7280"
                  className="flex-1"
                />
              </div>
            </div>

            {sources.length > 0 && (
              <div>
                <label className="label mb-2">
                  {t('clients.existingSources') || 'Existing Sources'}
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {sources.map((source) => (
                    <div
                      key={source._id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: source.color || '#6B7280' }}
                        />
                        <span className="font-medium">{translateSource(source.name)}</span>
                        {source.isSystem && (
                          <span className="text-xs text-gray-500">({t('clients.systemSource') || 'System'})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!source.isSystem && (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditSource(source)}
                            >
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSource(source._id, source.name)}
                              disabled={sourceLoading}
                            >
                              <TrashIcon className="w-4 h-4 text-danger" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={closeSourceModal}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={editingSource ? () => handleUpdateSource(editingSource._id) : handleCreateSource}
            loading={sourceLoading}
          >
            {editingSource ? t('common.update') : t('common.create')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
